import { Box, Button, styled } from '@mui/material';
import { PDFViewerPageChrome, PDFViewerViewMode } from './PDFViewer.props';

export const THUMBNAIL_WIDTH = 450;

export {
  FileViewerInitialLoadOverlay as PdfViewerInitialLoadOverlay,
  FileViewerLoadingSpinner as PdfViewerInitialLoadSpinner,
  FileViewerLoadingSpinner as PdfViewerResizeSpinner,
  FileViewerResizeOverlay as PdfViewerResizeOverlay,
  FileViewerToolbar as PdfViewerToolbar,
  FileViewerToolbarButton as PdfViewerToolbarButton,
  FileViewerToolbarDivider as PdfViewerToolbarDivider,
  FileViewerToolbarInput as PdfViewerToolbarInput,
  FileViewerToolbarSection as PdfViewerToolbarSection,
  FileViewerToolbarText as PdfViewerToolbarText,
} from '../viewer-controls';

export const PdfViewerToolbarActions = styled('div')({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 8,
  marginLeft: 'auto',
});

export const PdfViewerWrapper = styled('div')<{
  $viewMode?: PDFViewerViewMode;
  $pageChrome?: PDFViewerPageChrome;
  $isPannable?: boolean;
  $isPanning?: boolean;
  $nativeSelectionCopyFeedbackActive?: boolean;
  $nativeSelectionEnabled?: boolean;
}>(({
  $viewMode,
  $pageChrome,
  $isPannable,
  $isPanning,
  $nativeSelectionCopyFeedbackActive = false,
  $nativeSelectionEnabled = false,
}) => {
  const usesFloatingChrome = $viewMode === PDFViewerViewMode.DOCUMENT && $pageChrome === 'floating';
  const usesHeaderChrome = $viewMode === PDFViewerViewMode.DOCUMENT && $pageChrome === 'header';
  const nativeSelectionBackground = $nativeSelectionCopyFeedbackActive
    ? 'rgba(34, 197, 94, 0.34)'
    : 'rgba(3, 125, 232, 0.28)';
  const wrapperMinWidth = (() => {
    if (usesFloatingChrome) {
      return 0;
    }

    if (usesHeaderChrome) {
      return '100%';
    }

    return undefined;
  })();
  const wrapperWidth = (() => {
    if (usesFloatingChrome) {
      return '100%';
    }

    if (usesHeaderChrome) {
      return 'max-content';
    }

    return undefined;
  })();

  return {
    animation: usesFloatingChrome ? undefined : 'fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
    alignItems: $viewMode === PDFViewerViewMode.THUMBNAIL || usesHeaderChrome ? 'stretch' : 'center',
    background: usesFloatingChrome ? '#F3F8FD' : undefined,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: usesFloatingChrome ? '100%' : undefined,
    minHeight: usesFloatingChrome ? '100%' : undefined,
    minWidth: wrapperMinWidth,
    overflow: usesFloatingChrome ? 'hidden' : undefined,
    position: 'relative',
    userSelect: $isPanning ? 'none' : undefined,
    width: wrapperWidth,
    '&:hover': {
      cursor: $isPannable ? 'grab' : undefined,
    },
    '&[data-panning="true"], &[data-panning="true"]:hover': {
      cursor: 'grabbing',
    },
    '&[data-panning="true"] *': {
      cursor: 'grabbing !important',
    },
    '&[data-pannable="true"] .textLayer span, &[data-pannable="true"] .custom-text-layer [data-word-id]': {
      cursor: 'text',
    },
    '& .textLayer ::selection': {
      background: $nativeSelectionEnabled ? nativeSelectionBackground : undefined,
      color: $nativeSelectionEnabled ? 'transparent' : undefined,
    },
    '& .textLayer ::-moz-selection': {
      background: $nativeSelectionEnabled ? nativeSelectionBackground : undefined,
      color: $nativeSelectionEnabled ? 'transparent' : undefined,
    },
    '@keyframes fadeInUp': {
      '0%': {
        opacity: 0,
        transform: 'translateY(30px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
  };
});

export const PdfViewerScrollContainer = styled('div')<{
  $isPannable?: boolean;
  $isPanning?: boolean;
  $isResizing?: boolean;
}>(({ $isPannable, $isPanning, $isResizing }) => ({
  background: 'linear-gradient(180deg, #F3F8FD 0%, #EEF4F8 100%)',
  boxSizing: 'border-box',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: $isResizing ? 'hidden' : 'auto',
  padding: '0 16px 28px',
  position: 'relative',
  scrollbarGutter: 'stable',
  userSelect: $isPanning ? 'none' : undefined,
  width: '100%',
  '&:hover': {
    cursor: $isPannable ? 'grab' : undefined,
  },
  '&[data-panning="true"], &[data-panning="true"]:hover': {
    cursor: 'grabbing',
  },
  '&[data-panning="true"] *': {
    cursor: 'grabbing !important',
  },
}));

export const PdfViewerContentWrapper = styled('div')<{
  $isHidden?: boolean;
  $isResizing?: boolean;
  $thumbnailWidth?: number;
  $viewMode: PDFViewerViewMode;
  $pageChrome?: PDFViewerPageChrome;
}>(({ $isHidden, $isResizing, $thumbnailWidth, $viewMode, $pageChrome }) => {
  const isThumbnail = $viewMode === PDFViewerViewMode.THUMBNAIL;
  const usesFloatingChrome = $viewMode === PDFViewerViewMode.DOCUMENT && $pageChrome === 'floating';
  const contentGap = usesFloatingChrome ? '28px' : '10px';
  const contentPadding = usesFloatingChrome ? '16px 0 24px' : undefined;
  const contentOpacity = (() => {
    if ($isHidden) {
      return 0;
    }

    if ($isResizing) {
      return 0.72;
    }

    return 1;
  })();
  const isDocument = $viewMode === PDFViewerViewMode.DOCUMENT;
  const contentWidth = (() => {
    if (isThumbnail) {
      return '100%';
    }

    if (isDocument) {
      return 'max-content';
    }

    return undefined;
  })();
  const thumbnailColumnWidth = Math.max(1, Math.round($thumbnailWidth ?? THUMBNAIL_WIDTH));

  return {
    alignItems: isDocument ? 'center' : undefined,
    display: isThumbnail ? 'grid' : 'flex',
    flexDirection: isThumbnail ? undefined : 'column',
    gap: isThumbnail ? '20px' : contentGap,
    gridTemplateColumns: isThumbnail
      ? `repeat(auto-fit, minmax(${thumbnailColumnWidth}px, ${thumbnailColumnWidth}px))`
      : undefined,
    filter: $isResizing ? 'blur(2.5px) saturate(0.92)' : undefined,
    justifyContent: isThumbnail ? 'center' : undefined,
    justifyItems: isThumbnail ? 'center' : undefined,
    maxWidth: isThumbnail ? '100%' : undefined,
    minWidth: isDocument ? '100%' : undefined,
    overflowX: isThumbnail ? 'hidden' : undefined,
    opacity: contentOpacity,
    padding: isThumbnail ? '20px' : contentPadding,
    pointerEvents: $isHidden || $isResizing ? 'none' : undefined,
    transition: usesFloatingChrome ? 'filter 140ms ease-out, opacity 140ms ease-out' : undefined,
    width: contentWidth,
  };
});

export const PdfViewerThumbnailWrapper = styled('div')<{ $viewMode: PDFViewerViewMode }>(({ $viewMode }) => ({
  display: $viewMode === PDFViewerViewMode.THUMBNAIL ? 'grid' : 'flex',
  flexDirection: $viewMode === PDFViewerViewMode.THUMBNAIL ? 'row' : 'column',
  gap: $viewMode === PDFViewerViewMode.THUMBNAIL ? '20px' : '10px',
  alignItems: 'center',
  height: '100%',
  overflow: 'auto',
}));

export const PdfViewerStateContainer = styled('div')({
  padding: '20px',
  textAlign: 'center',
});

export const PdfNativeFrame = styled('iframe')({
  backgroundColor: '#FFFFFF',
  border: 0,
  display: 'block',
  flex: '1 1 auto',
  height: '100%',
  minHeight: 0,
  width: '100%',
});

export const PdfPageViewerLoader = styled('div')({
  alignItems: 'center',
  alignSelf: 'stretch',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFE 100%)',
  border: '1px solid #D8E3EE',
  borderRadius: 10,
  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
  boxSizing: 'border-box',
  color: '#475569',
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 700,
  gap: 10,
  justifyContent: 'center',
  lineHeight: 1,
  margin: 16,
  minHeight: 240,
  padding: 24,
  textAlign: 'center',
  '&::before': {
    animation: 'pdfLoaderPulse 0.9s ease-in-out infinite',
    backgroundColor: '#037DE8',
    borderRadius: '50%',
    content: '""',
    height: 8,
    width: 8,
  },
  '@keyframes pdfLoaderPulse': {
    '0%, 100%': {
      opacity: 0.35,
      transform: 'scale(0.82)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
});

export const StyledCitationNavigatorWrapper = styled('div')({
  position: 'fixed',
  bottom: 24,
  right: 24,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(0,0,0,0.75)',
  color: '#fff',
  borderRadius: 20,
  padding: '4px 12px',
  fontSize: 13,
  zIndex: 1300,
  boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
});

export const StyledCitationNavButton = styled(Button)({
  minWidth: 32,
  padding: '2px 6px',
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.5)',
  fontSize: 16,
});

export const StyledHighlightableTextNotification = styled(Box)({
  position: 'fixed',
  bottom: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#1976d2',
  color: 'white',
  padding: '12px 24px',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 500,
  animation: 'slideUp 0.3s ease-out',
  cursor: 'pointer',
});
